import React, { useState, useEffect } from 'react';

export default function TileInfo({ 
  selectedTile, 
  gameData, 
  userId, 
  startAuction, 
  canStartAuction, 
  getCurrentPlayer, 
  getOwnerDisplayName, 
  getPlayerColor,
  // New loan-related props
  requestLoan,
  getBankInterestRate,
  getBankAvailableLoanAmount,
  getPlayerLoans,
  repayLoan,
  preparationPeriod
}) {
  const [loanAmount, setLoanAmount] = useState('');
  const [repayLoanId, setRepayLoanId] = useState('');
  const [repayAmount, setRepayAmount] = useState('');
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Update current time every second for loan countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);
  
  if (!selectedTile) return null;

  const getTileTypeName = (tileType) => {
    const tileNames = {
      3: 'Farm 🌾',
      4: 'Market 🏪',
      5: 'Mine ⛏️',
      6: 'Fishery 🐟',
      7: 'Port ⚓',
      8: 'Oil Rig 🛢️',
      9: 'Oil Well 🕳️',
      10: 'Lumber Mill 🏗️',
      11: 'Power Plant ⚡',
      12: 'Factory 🏭',
      13: 'Bank 🏦',
      14: 'Construction Company 🏢'
    };
    return tileNames[tileType] || 'Unknown';
  };

  const isSelectedTileInAuction = () => {
    return selectedTile && gameData.activeAuctions && gameData.activeAuctions[selectedTile.key];
  };

  const handleRequestLoan = () => {
    const amount = parseFloat(loanAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid loan amount');
      return;
    }
    
    const availableLoanAmount = getBankAvailableLoanAmount(selectedTile.key);
    if (amount > availableLoanAmount) {
      alert(`Bank only has $${availableLoanAmount.toLocaleString()} available for loans`);
      return;
    }
    
    requestLoan(selectedTile.key, amount);
    setLoanAmount('');
  };

  const handleRepayLoan = () => {
    const amount = parseFloat(repayAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid repayment amount');
      return;
    }
    
    const currentPlayer = getCurrentPlayer();
    if (!currentPlayer || currentPlayer.budget < amount) {
      alert('Insufficient budget for this repayment');
      return;
    }
    
    repayLoan(repayLoanId, amount);
    setRepayAmount('');
    setRepayLoanId('');
  };

  // Get loans from this specific bank with real-time expiration calculation
  const getBankLoansWithTimer = () => {
    return getPlayerLoans()
      .filter(loan => {
        // Only show loans that belong to the current user AND are from this specific bank
        return loan.isActive && 
               loan.bankTileKey === selectedTile.key && 
               loan.borrowerId === userId;
      })
      .map(loan => {
        const expirationTime = new Date(loan.loanExpirationTime).getTime();
        const timeRemaining = Math.max(0, Math.ceil((expirationTime - currentTime) / 1000));
        const minutesRemaining = Math.floor(timeRemaining / 60);
        const secondsRemaining = timeRemaining % 60;
        const isExpiringSoon = timeRemaining <= 300; // 5 minutes or less
        
        return {
          ...loan,
          timeRemaining,
          minutesRemaining,
          secondsRemaining,
          isExpiringSoon
        };
      });
  };

  return (
    <div style={{
      position: 'absolute',
      top: '10px',
      right: '10px',
      background: 'rgba(0,0,0,0.8)',
      color: 'white',
      padding: '15px',
      borderRadius: '5px',
      fontFamily: 'Arial, sans-serif',
      width: '250px',
      maxWidth: '250px',
      maxHeight: 'calc(100vh - 20px)', // Prevent extending below screen with 10px margin on top and bottom
      overflowY: 'auto' // Enable vertical scrolling
    }}>
      <h3 style={{ margin: '0 0 10px 0', color: '#FFD700', wordWrap: 'break-word' }}>
        {getTileTypeName(selectedTile.type)}
      </h3>
      <div style={{ wordWrap: 'break-word' }}>Position: ({selectedTile.x}, {selectedTile.y})</div>
      
      {selectedTile.owner ? (
        <div style={{ marginTop: '10px' }}>
          <div style={{ 
            color: selectedTile.owner.owner === userId ? '#90EE90' : '#FFB74D',
            wordWrap: 'break-word'
          }}>
            Owner: <span style={{ 
              color: selectedTile.owner.ownerColor || getPlayerColor(selectedTile.owner.owner), 
              fontWeight: 'bold',
              wordWrap: 'break-word'
            }}>
              {selectedTile.owner.ownerDisplayName || getOwnerDisplayName(selectedTile.owner.owner)}
            </span>
            {selectedTile.owner.owner === userId && ' (You)'}
          </div>
          <div style={{ fontSize: '0.9em', wordWrap: 'break-word' }}>
            Purchase Price: ${selectedTile.owner.purchasePrice?.toLocaleString()}
          </div>
          
          {/* Loan Options for Banks (Non-owners only) */}
          {selectedTile.type === 13 && selectedTile.owner.owner !== userId && (
            <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              backgroundColor: 'rgba(33, 150, 243, 0.1)', 
              borderRadius: '5px',
              wordWrap: 'break-word'
            }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#2196F3', wordWrap: 'break-word' }}>💳 Request Loan</h4>
              
              <div style={{ fontSize: '0.75em', marginBottom: '5px', color: '#C8E6C9', wordWrap: 'break-word' }}>
                Interest Rate: {getBankInterestRate(selectedTile.key)}% per minute
              </div>
              
              <div style={{ fontSize: '0.75em', marginBottom: '5px', color: '#C8E6C9', wordWrap: 'break-word' }}>
                Available: ${getBankAvailableLoanAmount(selectedTile.key)?.toLocaleString() || '0'}
              </div>
              
              <div style={{ fontSize: '0.75em', marginBottom: '10px', color: '#FFD54F', fontWeight: 'bold', wordWrap: 'break-word' }}>
                ⏰ Loan Duration: 10 minutes (auto-collected if unpaid)
              </div>
              
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                placeholder="Loan amount"
                style={{
                  width: '100%',
                  padding: '5px',
                  marginBottom: '5px',
                  borderRadius: '3px',
                  border: 'none',
                  fontSize: '0.75em',
                  boxSizing: 'border-box'
                }}
              />
              
              <button
                onClick={handleRequestLoan}
                disabled={!loanAmount || parseFloat(loanAmount) <= 0 || parseFloat(loanAmount) > getBankAvailableLoanAmount(selectedTile.key)}
                style={{
                  width: '100%',
                  padding: '5px',
                  backgroundColor: (!loanAmount || parseFloat(loanAmount) <= 0 || parseFloat(loanAmount) > getBankAvailableLoanAmount(selectedTile.key)) ? '#666' : '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: (!loanAmount || parseFloat(loanAmount) <= 0 || parseFloat(loanAmount) > getBankAvailableLoanAmount(selectedTile.key)) ? 'not-allowed' : 'pointer',
                  fontSize: '0.75em',
                  opacity: (!loanAmount || parseFloat(loanAmount) <= 0 || parseFloat(loanAmount) > getBankAvailableLoanAmount(selectedTile.key)) ? 0.6 : 1
                }}
              >
                Request Loan (10 min term)
              </button>
              
              {/* Show current loans from this bank and repayment options */}
              {getBankLoansWithTimer().length > 0 && (
                <div style={{ 
                  marginTop: '15px', 
                  paddingTop: '10px', 
                  borderTop: '1px solid rgba(33, 150, 243, 0.3)',
                  wordWrap: 'break-word'
                }}>
                  <div style={{ fontSize: '0.8em', marginBottom: '10px', color: '#2196F3', wordWrap: 'break-word' }}>💳 Your Loans from this Bank:</div>
                  
                  {getBankLoansWithTimer().map(loan => (
                    <div key={loan.id} style={{
                      marginBottom: '10px',
                      padding: '8px',
                      backgroundColor: loan.isExpiringSoon ? 'rgba(255, 87, 34, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                      borderRadius: '4px',
                      border: loan.isExpiringSoon ? '1px solid rgba(255, 87, 34, 0.5)' : '1px solid rgba(255, 193, 7, 0.3)',
                      wordWrap: 'break-word'
                    }}>
                      <div style={{ fontSize: '0.75em', color: '#FFE082', marginBottom: '5px', wordWrap: 'break-word' }}>
                        Remaining: ${loan.remainingAmount?.toLocaleString() || '0'}
                      </div>
                      <div style={{ fontSize: '0.7em', color: '#C8E6C9', marginBottom: '5px', wordWrap: 'break-word' }}>
                        Interest: {loan.interestRate}% per minute
                      </div>
                      <div style={{ 
                        fontSize: '0.7em', 
                        color: loan.isExpiringSoon ? '#FF6B6B' : '#FFD54F', 
                        marginBottom: '8px',
                        fontWeight: loan.isExpiringSoon ? 'bold' : 'normal',
                        wordWrap: 'break-word'
                      }}>
                        {loan.isExpiringSoon ? '⚠️ ' : '⏰ '}
                        Expires in: {loan.minutesRemaining}:{loan.secondsRemaining.toString().padStart(2, '0')}
                        {loan.isExpiringSoon && <div style={{fontSize: '0.65em', color: '#FF8A65'}}>Auto-collection imminent!</div>}
                      </div>
                      
                      <input
                        type="number"
                        value={repayLoanId === loan.id ? repayAmount : ''}
                        onChange={(e) => {
                          setRepayLoanId(loan.id);
                          setRepayAmount(e.target.value);
                        }}
                        placeholder="Repayment amount"
                        style={{
                          width: '100%',
                          padding: '4px',
                          marginBottom: '5px',
                          borderRadius: '3px',
                          border: 'none',
                          fontSize: '0.7em',
                          boxSizing: 'border-box'
                        }}
                      />
                      
                      <button
                        onClick={handleRepayLoan}
                        disabled={repayLoanId !== loan.id || !repayAmount || parseFloat(repayAmount) <= 0 || (getCurrentPlayer()?.budget || 0) < parseFloat(repayAmount || '0')}
                        style={{
                          width: '100%',
                          padding: '4px',
                          backgroundColor: (repayLoanId !== loan.id || !repayAmount || parseFloat(repayAmount) <= 0 || (getCurrentPlayer()?.budget || 0) < parseFloat(repayAmount || '0')) ? '#666' : '#FF9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: (repayLoanId !== loan.id || !repayAmount || parseFloat(repayAmount) <= 0 || (getCurrentPlayer()?.budget || 0) < parseFloat(repayAmount || '0')) ? 'not-allowed' : 'pointer',
                          fontSize: '0.7em',
                          opacity: (repayLoanId !== loan.id || !repayAmount || parseFloat(repayAmount) <= 0 || (getCurrentPlayer()?.budget || 0) < parseFloat(repayAmount || '0')) ? 0.6 : 1
                        }}
                      >
                        {repayLoanId === loan.id && repayAmount 
                          ? `Repay $${Math.min(parseFloat(repayAmount || '0'), loan.remainingAmount || 0).toLocaleString()}`
                          : 'Repay Loan'
                        }
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Show total loans summary */}
              {getPlayerLoans().length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '0.7em', color: '#FFB74D', wordWrap: 'break-word' }}>
                  Total active loans: {getPlayerLoans().length}
                </div>
              )}
            </div>
          )}
        </div>
      ) : isSelectedTileInAuction() ? (
        <div style={{ color: '#FF5722', marginTop: '10px', wordWrap: 'break-word' }}>
          Currently in auction!
        </div>
      ) : (
        <div style={{ marginTop: '15px' }}>
          <div style={{ marginBottom: '10px', fontSize: '0.9em', wordWrap: 'break-word' }}>
            Starting Price: $10,000
          </div>
          
          <div style={{ 
            marginBottom: '10px', 
            fontSize: '0.8em',
            color: canStartAuction() ? '#90EE90' : '#FF6B6B',
            wordWrap: 'break-word'
          }}>
            Your Budget: ${getCurrentPlayer()?.budget.toLocaleString() || '0'}
          </div>
          
          {!canStartAuction() && (
            <div style={{
              marginBottom: '10px',
              padding: '8px',
              backgroundColor: 'rgba(255, 87, 34, 0.2)',
              border: '1px solid #FF5722',
              borderRadius: '4px',
              fontSize: '0.8em',
              color: '#FF8A65',
              wordWrap: 'break-word'
            }}>
              {preparationPeriod?.isInPreparation 
                ? "Wait for the preparation period to end!" 
                : "⚠️ Insufficient funds! Need $10,000 to start auction."}
            </div>
          )}
          
          <button 
            onClick={startAuction}
            disabled={!canStartAuction() || (Object.keys(gameData.activeAuctions || {}).length > 0 && gameData.activeAuctions[selectedTile.key])}
            style={{
              padding: '8px 15px',
              backgroundColor: 
                !canStartAuction() ? '#666' :
                (gameData.activeAuctions && gameData.activeAuctions[selectedTile.key]) ? '#666' : '#FF9800',
              color: !canStartAuction() ? '#999' : 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 
                !canStartAuction() ? 'not-allowed' :
                (gameData.activeAuctions && gameData.activeAuctions[selectedTile.key]) ? 'not-allowed' : 'pointer',
              width: '100%',
              opacity: !canStartAuction() ? 0.6 : 1,
              fontSize: '0.9em',
              wordWrap: 'break-word'
            }}
          >
            {preparationPeriod?.isInPreparation ? 'Wait' :
             !canStartAuction() ? 'Insufficient Funds' :
             (gameData.activeAuctions && gameData.activeAuctions[selectedTile.key]) ? 'Auction Active' : 'Start Auction'}
          </button>
        </div>
      )}
    </div>
  );
}